import React, { useState } from "react";
import { FaEnvelope, FaPhoneAlt } from "react-icons/fa";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    schoolYear: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact-us/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ success: true, message: data.message });
        // Reset form on success
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          schoolYear: "",
          message: ""
        });
      } else {
        setSubmitStatus({ success: false, message: data.message });
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: "Network error. Please try again later."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-teal-50 py-10 md:py-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto text-center mb-8 md:mb-12">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#1E1032] mb-3 md:mb-4">
          We'd Like To Hear From You
        </h2>
        <p className="text-[#4B4453] text-sm sm:text-base md:text-lg max-w-2xl mx-auto">
          Let us know how we can help you. We're here to answer your questions and support your child's educational journey.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row items-start justify-center gap-6 md:gap-10">
        {/* Info Card */}
        <div className="bg-[#1E1032] hover:bg-[#2a1a3f] text-white p-6 md:p-8 rounded-2xl md:rounded-3xl w-full lg:w-1/3 shadow-lg md:shadow-xl">
          <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Get In Touch</h3>
          <p className="text-xs sm:text-sm leading-relaxed mb-4 md:mb-6">
            We’d love to hear from you. Whether you have questions about our programs, need help with registration, or want to know how we can support your child’s OC /Selective preparation—our team is here to assist.
          </p>
          <p className="text-xs sm:text-sm leading-relaxed mb-4 md:mb-6">
            Fill out the form below or reach out via email, and we’ll get back to you as soon as possible.
          </p>
          <div className="space-y-3 md:space-y-4">
            <a href="mailto:info@examwarriors.com.au" className="flex items-center gap-2 md:gap-3 hover:underline">
              <FaEnvelope className="text-yellow-400 text-sm md:text-base" />
              <span className="text-xs sm:text-sm font-medium">info@examwarriors.com.au</span>
            </a>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white w-full lg:w-[60%] p-6 md:p-8 rounded-2xl md:rounded-3xl shadow-lg md:shadow-xl space-y-4 md:space-y-6">
          {submitStatus && (
            <div className={`p-3 md:p-4 rounded-lg text-sm md:text-base ${submitStatus.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {submitStatus.message}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="John"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Doe"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(123) 456-7890"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Select School Year</label>
            <div className="flex flex-wrap gap-2 md:gap-4">
              {["Year 3", "Year 4", "Year 5", "Year 6"].map((year, index) => (
                <label key={index} className="inline-flex items-center gap-1 md:gap-2 text-xs sm:text-sm text-gray-700">
                  <input
                    type="radio"
                    name="schoolYear"
                    value={year}
                    checked={formData.schoolYear === year}
                    onChange={handleChange}
                    className="accent-purple-400"
                  />
                  {year}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Message *</label>
            <textarea
              rows="4"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Write your message..."
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`${isSubmitting ? 'bg-yellow-600' : 'bg-yellow-400 hover:bg-orange-500'} text-white font-semibold px-5 py-2 md:px-6 md:py-3 rounded-full transition duration-300 flex items-center justify-center gap-2 w-full text-sm md:text-base`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactUs;